import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, Save, X } from 'lucide-react';
import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import { getPromptById, updatePrompt } from '../../services/wordpressService';

// Prompt type options with display labels and internal values
const PROMPT_TYPES = [
  { label: 'WordPress Article', value: 'wordpress article' },
  { label: 'Facebook CTA', value: 'facebook cta' },
  { label: 'Facebook Post', value: 'facebook post' },
  { label: 'Pinterest CTA', value: 'pinterest cta' },
  { label: 'Image', value: 'image' },
  { label: 'Image Description', value: 'image description' },
  { label: 'Title', value: 'title' },
  { label: 'Keyword', value: 'keyword' },
];

export default function EditPrompt() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    promptType: '',
    name: '',
    content: '',
  });

  // Load existing prompt data
  useEffect(() => {
    loadPrompt();
  }, [id]);

  const loadPrompt = async () => {
    try {
      setIsLoading(true);
      const prompt = await getPromptById(id);
      
      setFormData({
        promptType: prompt?.promptType || '',
        name: prompt?.name || '',
        content: prompt?.content || '',
      });
      
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load prompt');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedPromptLabel = PROMPT_TYPES?.find(pt => pt?.value === formData?.promptType)?.label || 'Select Type';

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    // Validation
    if (!formData?.promptType) {
      setError('Please select a prompt type');
      return;
    }
    if (!formData?.name?.trim()) {
      setError('Please enter a prompt name');
      return;
    }
    if (!formData?.content?.trim()) {
      setError('Please enter prompt content');
      return;
    }

    setIsSaving(true);

    try {
      await updatePrompt(id, {
        name: formData?.name?.trim(),
        content: formData?.content?.trim(),
        promptType: formData?.promptType,
      });

      // Redirect to prompts list
      navigate('/tools/prompts');
    } catch (err) {
      setError(err?.message || 'Failed to update prompt');
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/tools/prompts');
  };

  if (isLoading) {
    return (
      <SidebarProvider>
        <div className="min-h-screen bg-background">
          <Sidebar />
          <main className="main-content">
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <main className="main-content">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-text">Edit Prompt</h1>
              <p className="text-text-secondary mt-2">
                Modify your AI prompt template
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-500 text-sm">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Prompt Type Dropdown */}
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  Prompt Type <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-left flex items-center justify-between hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                  >
                    <span className={formData?.promptType ? 'text-text' : 'text-text-secondary'}>
                      {selectedPromptLabel}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-text-secondary transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute z-10 w-full mt-2 bg-white border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {PROMPT_TYPES?.map((type) => (
                        <button
                          key={type?.value}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, promptType: type?.value });
                            setIsDropdownOpen(false);
                            setError('');
                          }}
                          className={`w-full px-4 py-3 text-left text-gray-900 hover:bg-gray-100 transition-colors ${
                            formData?.promptType === type?.value ? 'bg-gray-50 font-medium' : 'bg-white'
                          }`}
                        >
                          {type?.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Prompt Name */}
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  Prompt Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  placeholder="Enter a descriptive name for this prompt"
                  className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-text placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all"
                />
              </div>

              {/* Prompt Content */}
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  Prompt Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData?.content}
                  onChange={(e) => setFormData({ ...formData, content: e?.target?.value })}
                  placeholder="Enter your prompt template here..."
                  rows={12}
                  className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-text placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all resize-y"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 px-6 py-3 bg-primary hover:bg-primary-hover text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="flex-1 px-6 py-3 bg-surface hover:bg-surface-hover border border-border text-text font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <X className="w-5 h-5" />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}